// Define types based on the Fabrik API schemas
export enum GatheringStatus {
  NEW = "New",
  IN_REVIEW = "In Review",
  NEED_MORE_INFO = "Need More Info",
  RESCHEDULE = "Reschedule",
  DENIED = "Denied",
  AWAITING_GATHERING_LINK = "Awaiting Gathering Link",
  READY_TO_PUBLISH = "Ready to Publish",
  PUBLISHED = "Published"
}

export enum Interest {
  READING_WRITING = "Reading & Writing",
  CAREER_NETWORKING = "Career & Networking",
  CREATIVE_ARTS = "Creative Arts",
  HEALTH_WELLNESS = "Health and Wellness",
  TECHNOLOGY_AI = "Technology & AI",
  FINANCIAL = "Financial",
  EDUCATIONAL = "Educational",
  OTHER = "Other"
}

export enum SpaceType {
  HUB = "Hub",
  SPOKE = "Spoke"
}

export enum Timezone {
  UTC = "UTC",
  GMT = "GMT",
  EST = "EST",
  EDT = "EDT",
  CST = "CST",
  CDT = "CDT",
  MST = "MST",
  MDT = "MDT",
  PST = "PST",
  PDT = "PDT"
  // Add other timezones as needed
}

export interface Address {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

export interface TimezoneDetails {
  short_name: string;
  description: string;
  iana_name: string;
  offset_str: string;
}

export interface Space {
  id: string;
  name: string;
  description?: string;
  image_object_key?: string;
  image_url?: string;
  latitude?: number;
  longitude?: number;
  capacity?: number;
  timezone: Timezone;
  timezone_details?: TimezoneDetails;
  type?: SpaceType;
  address?: Address;
  distance?: number;
}

export interface Area {
  id: string;
  name: string;
  capacity: number;
  min_capacity: number;
  description?: string;
}

export interface User {
  uid: string;
  displayName: string | null;
  email: string | null;
  token: string;
  isAdmin: boolean;
  role?: "User" | "Admin" | "Manager";

}

// HubSpot integration interfaces
export interface HubSpotContactProperties {
  createdate?: string;
  email?: string;
  firstname?: string;
  hs_object_id?: string;
  lastmodifieddate?: string;
  lastname?: string;
  access_tier?: string;
  fabrik_location?: string;
  fabrik_lifecycle_stage?: string;
}

export interface HubSpotContact {
  id: string;
  createdAt?: string;
  updatedAt?: string;
  archived?: boolean;
  properties: HubSpotContactProperties;
  subscriptions?: SubscriptionDetails[];
}

// Subscription details interface
export interface SubscriptionDetails {
  product_name: string;
  hubspot_subscription_id: string;
  next_billing_date?: string;
  next_payment_amount?: string;
  subscription_status?: string;
}

// Payment method enum to match API
export enum PaymentMethod {
  CREDIT_CARD = "Credit Card",
  PAYPAL = "Paypal",
  BANK_TRANSFER = "Bank Transfer",
  APPLE_PAY = "Apple Pay",
  GOOGLE_PAY = "Google Pay",
}

// Updated FabrikUser interface to match API UserResponse schema
export interface FabrikUser {
  id: string;
  firebase_id?: string;
  display_name?: string;
  email: string;
  email_verified: boolean;
  first_name?: string;
  last_name?: string;
  image_object_key?: string;
  image_url?: string;
  onboarding_done: boolean;
  has_password: boolean;
  access_tier?: string;
  membership_status?: string;
  payment_method?: PaymentMethod;
  interests?: Interest[];
  expectations?: string;
  push_notification_token?: string;
  space?: {
    id: string;
    name: string;
  };
  address?: Address;
  hubspot?: HubSpotContact;
  subscription_details?: SubscriptionDetails[];
  // Usage data from consolidated API
  usage_data?: {
    active_day_passes_count: number;
    available_days: number | null; // null = unlimited
    membership_tier: string | null;  
    total_checkins_count?: number | null;
    gathering_checkins_count?: number | null;
    last_gathering_checkin?: string | null; // ISO timestamp
    last_space_checkin?: string | null; // ISO timestamp
    hosted_gatherings_count?: number | null;
    last_hosted_gathering?: string | null; // ISO timestamp
    gatherings_attended_this_month?: number;
    space_checkins_this_month?: number;
    gatherings_hosted_this_cycle?: number | null;
  };
  // Admin notes count
  notes_count?: number;
  // Legacy field for backward compatibility
  membership?: string;
  // CaaS (Community as a Service) data
  caas_data?: {
    is_caas_user: boolean;
    teams: Array<{
      team_id: string;
      team_name: string;
      client_name: string;
    }>;
  } | null;
  // Communication opt-in status (CaaS users)
  opted_in_comms?: boolean | null;
  // User account creation timestamp (join date)
  created_at?: string;
}

/**
 * Lifecycle of a host-submitted edit staged against a PUBLISHED gathering
 * (ENG-856). The backend ships `pending` | `approved` | `rejected` today and
 * ENG-1096 adds `expired` and `superseded`.
 *
 * Deliberately a plain `string` rather than a union of the values we know
 * about: the API deploys independently of this dashboard, so a build compiled
 * before a new status exists must still render whatever it is handed. Always
 * run the value through `describePendingEditStatus`
 * (src/features/gatherings/utils/pendingEdit.ts), which has an
 * unknown-value fallback, instead of switching on it inline.
 */
export type GatheringPendingEditStatus = string;

/**
 * Statuses this build knows how to label. Not a validation list — an unknown
 * status renders through the fallback rather than being rejected.
 */
export const KNOWN_PENDING_EDIT_STATUSES = [
  'pending',
  'approved',
  'rejected',
  // ENG-1096 (not live yet — rendering is ready ahead of the backend).
  'expired',
  'superseded'
] as const;

/**
 * The only gathering fields the backend will ever stage into a pending edit
 * (whitelisted server-side in api/domain/gathering_edit_types.py).
 */
export const PENDING_EDIT_FIELDS = [
  'title',
  'description',
  'image_object_key',
  'category',
  'community',
  'additional_notes',
  'is_public',
  'is_members_only'
] as const;

export type PendingEditField = (typeof PENDING_EDIT_FIELDS)[number];

/**
 * A staged, not-yet-applied edit to a PUBLISHED gathering (ENG-856).
 *
 * The top-level fields on `Gathering` still hold the LIVE, currently
 * registerable values. A field is proposed for change if and only if it is a
 * key in `changes`, so the diff is always
 * `gathering[field]` → `changes[field]`.
 */
export interface GatheringPendingEdit {
  id: string;
  status: GatheringPendingEditStatus;
  /** `{field_name: proposed_new_value}` — permissive JSONB document. */
  changes: Record<string, unknown>;
  submitted_by: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Gathering {
  id: string;
  title: string;
  description: string;
  image_object_key?: string;
  image_url?: string;
  date: string;
  start_time: string;
  end_time: string;
  duration: number;
  category: Interest;
  event_size: number;
  additional_notes?: string;
  is_public: boolean;
  is_members_only: boolean;
  is_featured: boolean;
  is_fabrik_hosted: boolean;
  is_builder_gathering: boolean;
  community: string;
  luma_link?: string;
  status: GatheringStatus;
  source?: "web" | "mobile";
  created_at?: string; // ISO 8601 datetime string from backend
  space?: Space;
  areas?: Area[];
  owner?: {
    id: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
  };
  user_interested?: boolean;
  company_name?: string | null; // Freeform company name captured at kiosk check-in (present on attended-gathering listings)
  // ENG-856 / ENG-1097: only populated by the authenticated single-gathering
  // read (GET /gatherings/{id}) for the owner or an admin. The paginated
  // /gatherings/search listing always leaves these unset, which is why the
  // board has to sweep per gathering — see usePendingEditSweep.
  has_pending_edit?: boolean;
  pending_edit?: GatheringPendingEdit | null;
}

export const DragItemTypes = {
  GATHERING: 'gathering'
};

// Day Pass types for user usage data
export interface DayPassResponse {
  id: string;
  user_id: string;
  purchase_date: string; // ISO datetime string
  status: string;
  expires_on: string; // ISO datetime string
  hubspot_deal_id: string;
  used_on?: string; // ISO datetime string, optional
}

// Check-in types for Available Days calculation
export interface CheckInResponse {
  id: string;
  user_id: string;
  space_id: string;
  used_quota_type: string;
  timestamp: string; // ISO datetime string
  // Optional enriched data from backend
  user?: {
    id: string;
    first_name?: string;
    last_name?: string;
    display_name?: string;
    email: string;
  };
  space?: {
    id: string;
    name: string;
  };
}

// Paginated response for check-in search
export interface CheckInPaginatedResponse {
  items: CheckInResponse[];
  total_count: number;
  total_pages: number;
  current_page: number;
  order_by?: string;
  order_direction?: 'asc' | 'desc';
}

// Membership tier types for Available Days calculation
export interface MembershipTierResponse {
  id: string;
  name: string;
  days_in_space: number | null; // null means unlimited
  gatherings_host_limit: number | null;
}

// User usage data aggregated for display on user cards
export interface UserUsageData {
  userId: string;
  activeDayPassesCount: number;
  availableDays?: number | null; // Available membership days (null = unlimited, undefined = not calculated)
  membershipTier?: string | null; // Current membership tier name (null = no tier)
  isLoading: boolean;
  error?: string;
}

// Admin check-in types for Step 7 implementation
export interface AdminCheckInRequest {
  user_id: string;
  space_id: string;
  use_day_pass: boolean;
}

export interface CheckInFullResponse {
  status: "success" | "already_checked_in" | "no_quota" | "no_day_pass" | "error";
  data?: CheckInResponse;
  message?: string;
  has_day_pass?: boolean;
  quota_type?: string;
  has_membership?: boolean;
}

export interface GatheringCheckIn {
  id: string;
  timestamp: string;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  company_name?: string | null; // Freeform company name captured at kiosk check-in
}

// User Notes types
export interface UserNoteResponse {
  id: string;
  author_id: string | null;
  subject_id: string;
  text: string;
  is_task: boolean;
  is_done: boolean;
  created_at: string; // ISO datetime string
  updated_at: string; // ISO datetime string
}

export interface UserNoteCreate {
  subject_id: string;
  author_id?: string | null;
  text: string;
  is_task: boolean;
  is_done: boolean;
}

export interface UserNoteUpdate {
  text?: string;
  is_task?: boolean;
  is_done?: boolean;
}

export interface FormAnswerValue {
  answer: string;
}

export interface FormAnswerItem {
  id: string;
  user_id: string;
  question_id: string;
  external_response_id: string;
  source: string;
  value: FormAnswerValue;
  submitted_at: string | null;
  question_title: string | null;
  form_title: string | null;
  form_external_id: string | null;
}

export interface FormAnswersResponse {
  total_count: number;
  total_pages: number;
  current_page: number;
  items: FormAnswerItem[];
}

export interface SlackProfileField {
  element_id: string;
  external_id: string;
  name: string;
  field_type: string;
  value: string;
}

export interface SlackProfileResponse {
  status: string;
  user_id: string;
  profile: SlackProfileField[];
}

export interface ActionItem {
  action: string;
  reasoning: string;
  task_type: string;
}

// User profile update payload for admin updates
export interface UpdateUserProfilePayload {
  first_name?: string;
  last_name?: string;
  display_name?: string;
}

// Building types - match backend schemas
export interface MinimalBuilding {
  id: string;
  name: string;
  address: string;
}

export interface Building {
  id: string;
  name: string;
  address: string;
  timezone: string;
  latitude: number;
  longitude: number;
  marked_as_deleted: boolean;
}

export interface BuildingSearchRequest {
  name?: string;
  page?: number;
  limit?: number;
}

export interface BuildingPaginatedResponse {
  items: Building[];
  total_count: number;
  total_pages: number;
  current_page: number;
}

export interface BuildingCreateRequest {
  name: string;
  address: string;
  timezone: string;
  latitude: number;
  longitude: number;
}

export interface BuildingUpdateRequest {
  name?: string;
  address?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
}

// CaaS (Community as a Service) types - match backend schemas exactly
export interface CaasClient {
  id: string;
  name: string;
  address: string;
  hubspot_subscription_id?: string;
  marked_as_deleted: boolean;
}

export interface CaasTeam {
  id: string;
  name: string;
  address: string;
  building_id?: string | null;
  building?: MinimalBuilding | null;
  caas_client_id: string;
  domains?: string[];
  marked_as_deleted: boolean;
}

export interface CaasClientCreateRequest {
  name: string;
  address: string;
  hubspot_subscription_id?: string;
}

export interface CaasClientUpdateRequest {
  name?: string;
  address?: string;
  hubspot_subscription_id?: string;
}

export interface CaasTeamCreateRequest {
  name: string;
  address: string;
  building_id?: string | null;
  caas_client_id: string;
  domains?: string[];
}

export interface CaasTeamUpdateRequest {
  name?: string;
  address?: string;
  building_id?: string | null;
  caas_client_id?: string;
  domains?: string[];
}

// Building matcher types for bulk import
export interface BuildingMatchRequest {
  names: string[];
}

export interface BuildingMatchItem {
  input_name: string;
  matched_building: MinimalBuilding | null;
  confidence: 'exact' | 'starts_with' | 'contains' | 'none' | null;
}

export interface BuildingMatchResponse {
  matches: BuildingMatchItem[];
}

// Bulk team import types
export interface BulkTeamImportItem {
  name: string;
  building_id: string;
  domains?: string[];
}

export interface BulkTeamImportRequest {
  teams: BulkTeamImportItem[]; // min 1, max 500
}

export interface BulkTeamImportResultItem {
  row_index: number;
  team_name: string;
  success: boolean;
  team_id?: string; // UUID if successful
  error?: string; // User-friendly error message
  error_detail?: string; // Technical details for debugging
}

export interface BulkTeamImportResponse {
  total: number;
  successful: number;
  failed: number;
  results: BulkTeamImportResultItem[];
}

// CSV parsing types for bulk import UI
export interface CSVColumnMapping {
  teamName: string | null; // CSV column name for team name
  buildingName: string | null; // CSV column name for building name
  domains: string | null; // CSV column name for domains (optional)
}

export interface CSVRowData {
  rowIndex: number; // Original row number from CSV (for error reporting)
  [key: string]: any; // Dynamic keys based on CSV columns
}
